// frontend/src/utils/notificationSound.js

/**
 * Utility for playing notification sounds
 * Uses Web Audio API for better performance and control
 */
class NotificationSound {
  constructor() {
    this.audioContext = null;
    this.audioUnavailable = false;
    this.soundEnabled = this.getSoundPreference();
  }

  /**
   * Initialize audio context (required for modern browsers)
   * Call this on user interaction (e.g., button click)
   */
  init() {
    if (this.audioContext || this.audioUnavailable) {
      return this.audioContext;
    }

    try {
      const AudioContext = typeof window !== 'undefined'
        ? window.AudioContext || window.webkitAudioContext
        : null;

      if (!AudioContext) {
        this.markAudioUnavailable();
        return null;
      }

      this.audioContext = new AudioContext();
      return this.audioContext;
    } catch (error) {
      this.markAudioUnavailable();
      return null;
    }
  }

  /**
   * Stop retrying Web Audio after the browser reports unsupported or failed
   * device/renderer state. Audio hardware availability is outside app control,
   * so repeated app-level retries only create noisy console failures.
   */
  markAudioUnavailable() {
    this.audioUnavailable = true;
    this.audioContext = null;
  }

  /**
   * Resolve the current audio context lazily from actual playback paths.
   */
  getAudioContext() {
    if (!this.soundEnabled) {
      return null;
    }

    if (!this.audioContext) {
      return this.init();
    }

    return this.audioContext;
  }

  /**
   * Resume suspended contexts only from explicit user-gesture unlock paths.
   * Notification playback must not call this for a suspended context because
   * browsers can resolve a pending resume on a later unrelated gesture, which
   * would play a stale notification sound.
   */
  resumeIfSuspended(audioContext) {
    if (audioContext.state !== 'suspended') {
      return true;
    }

    try {
      const resumeResult = audioContext.resume();
      if (resumeResult && typeof resumeResult.then === 'function') {
        resumeResult.catch(() => {
          // Resume can be rejected by transient browser autoplay policy when a
          // notification arrives outside an active user gesture. Keep the context
          // recoverable so a later user-initiated playback attempt can resume it.
        });
      }
    } catch (error) {
      // Synchronous resume failures should not make autoplay-policy failures
      // look like permanent hardware errors. Skip this sound and allow future
      // gesture-based attempts to retry.
    }

    return false;
  }

  /**
   * Unlock Web Audio from a real user gesture. Browser autoplay policy can
   * reject notification-triggered resumes outside activation, so this keeps the
   * context recoverable and gives the app one explicit gesture-based resume
   * path without treating policy rejections as permanent device failures.
   */
  unlockFromUserGesture() {
    if (!this.soundEnabled) {
      return false;
    }

    const audioContext = this.init();

    if (audioContext) {
      this.resumeIfSuspended(audioContext);
      return true;
    }

    return this.audioUnavailable;
  }

  /**
   * Play a notification sound
   * @param {string} type - Type of notification (info, success, warning, error)
   */
  play(type = 'info') {
    const audioContext = this.getAudioContext();

    if (!audioContext || audioContext.state === 'suspended') {
      return;
    }

    try {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Different sounds for different notification types
      switch (type) {
        case 'success':
          oscillator.frequency.value = 800;
          gainNode.gain.value = 0.1;
          oscillator.type = 'sine';
          break;
        case 'warning':
          oscillator.frequency.value = 600;
          gainNode.gain.value = 0.15;
          oscillator.type = 'triangle';
          break;
        case 'error':
          oscillator.frequency.value = 400;
          gainNode.gain.value = 0.2;
          oscillator.type = 'square';
          break;
        case 'info':
        default:
          oscillator.frequency.value = 700;
          gainNode.gain.value = 0.1;
          oscillator.type = 'sine';
          break;
      }

      // Fade in and out for smoother sound
      const now = audioContext.currentTime;
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(gainNode.gain.value, now + 0.05);
      gainNode.gain.linearRampToValueAtTime(0, now + 0.3);

      oscillator.start(now);
      oscillator.stop(now + 0.3);
    } catch (error) {
      this.markAudioUnavailable();
    }
  }

  /**
   * Play a multi-tone notification for important alerts
   */
  playImportant() {
    const audioContext = this.getAudioContext();

    if (!audioContext || audioContext.state === 'suspended') {
      return;
    }

    try {
      const notes = [523, 659, 784]; // C, E, G chord
      const duration = 0.15;

      notes.forEach((frequency, index) => {
        setTimeout(() => {
          try {
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.value = frequency;
            oscillator.type = 'sine';

            const now = audioContext.currentTime;
            gainNode.gain.setValueAtTime(0, now);
            gainNode.gain.linearRampToValueAtTime(0.15, now + 0.02);
            gainNode.gain.linearRampToValueAtTime(0, now + duration);

            oscillator.start(now);
            oscillator.stop(now + duration);
          } catch (error) {
            this.markAudioUnavailable();
          }
        }, index * 100);
      });
    } catch (error) {
      this.markAudioUnavailable();
    }
  }

  /**
   * Enable or disable sound
   * @param {boolean} enabled
   */
  setEnabled(enabled) {
    this.soundEnabled = enabled;
    localStorage.setItem('notificationSoundEnabled', enabled ? 'true' : 'false');

    if (enabled) {
      // Enabling sound commonly happens inside a click handler. Use that same
      // activation to unlock Web Audio instead of waiting for a later gesture.
      this.unlockFromUserGesture();
    }
  }

  /**
   * Get sound preference from localStorage
   * @returns {boolean}
   */
  getSoundPreference() {
    const pref = localStorage.getItem('notificationSoundEnabled');
    return pref !== 'false'; // Default to true if not set
  }

  /**
   * Toggle sound on/off
   * @returns {boolean} New state
   */
  toggle() {
    const newState = !this.soundEnabled;
    this.setEnabled(newState);
    return newState;
  }
}

// Create singleton instance
const notificationSound = new NotificationSound();

// Prime/resume Web Audio from the first real user gesture. This restores sound
// after normal autoplay-policy suspension while keeping playback paths lazy.
if (typeof document !== 'undefined') {
  const handleAudioUnlockGesture = () => {
    if (notificationSound.unlockFromUserGesture()) {
      document.removeEventListener('click', handleAudioUnlockGesture, true);
      document.removeEventListener('touchstart', handleAudioUnlockGesture, true);
    }
  };

  document.addEventListener('click', handleAudioUnlockGesture, true);

  document.addEventListener('touchstart', handleAudioUnlockGesture, {
    capture: true,
    passive: true
  });
}

export default notificationSound;
